import { useEffect, useState } from 'preact/hooks';
import { api } from '../../api/client';
import type { AccountDto, AccountVisibilityDto } from '../../api/types';
import { ErrorTile, Eyebrow } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { useTranslation } from '../../i18n';

type Visibility = 'Public' | 'Unlisted' | 'Private';

interface VisibilityRowDef {
    key: keyof AccountVisibilityDto;
    labelKey: string;
    descriptionKey: string;
    noteKeys: Record<Visibility, string>;
}

const ROW_DEFS: VisibilityRowDef[] = [
    {
        key: 'profile',
        labelKey: 'me.visibility.profile.label',
        descriptionKey: 'me.visibility.profile.description',
        noteKeys: {
            Public: 'me.visibility.profile.notePublic',
            Unlisted: 'me.visibility.profile.noteUnlisted',
            Private: 'me.visibility.profile.notePrivate',
        },
    },
    {
        key: 'favorites',
        labelKey: 'me.visibility.favorites.label',
        descriptionKey: 'me.visibility.favorites.description',
        noteKeys: {
            Public: 'me.visibility.favorites.notePublic',
            Unlisted: 'me.visibility.favorites.noteUnlisted',
            Private: 'me.visibility.favorites.notePrivate',
        },
    },
    {
        key: 'collections',
        labelKey: 'me.visibility.collections.label',
        descriptionKey: 'me.visibility.collections.description',
        noteKeys: {
            Public: 'me.visibility.collections.notePublic',
            Unlisted: 'me.visibility.collections.noteUnlisted',
            Private: 'me.visibility.collections.notePrivate',
        },
    },
];

/**
 * Visibility section of /me/account. Three rows (profile / favourites
 * / collections) × three values (Public / Unlisted / Private). Saving
 * one row patches just that key on the backend (the controller accepts
 * a partial body and only writes provided fields).
 */
export function AccountVisibilityPage() {
    const t = useTranslation();
    const [account, setAccount] = useState<AccountDto | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const toast = useToast();

    useEffect(() => {
        let cancelled = false;
        api.me.account
            .get()
            .then((a) => {
                if (!cancelled) setAccount(a);
            })
            .catch((e) => {
                if (!cancelled)
                    setLoadError(e instanceof Error ? e.message : String(e));
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onChange = async (key: keyof AccountVisibilityDto, value: Visibility) => {
        if (!account) return;
        if (account.visibility[key] === value) return;
        setBusyKey(key);
        try {
            const next = await api.me.account.setVisibility({ [key]: value });
            setAccount(next);
            toast.push('success', <>{t('me.visibility.saved')}</>);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', t('me.visibility.saveError', { msg }));
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <AccountShell
            title={t('me.visibility.title')}
            subtitle={t('me.visibility.subtitle')}
        >
            <div class="mx-auto w-full max-w-3xl space-y-8 px-[clamp(20px,3.5vw,56px)] py-10">
                {loadError && (
                    <ErrorTile title={t('me.visibility.loadError')} details={loadError} />
                )}
                {!account && !loadError && (
                    <p class="text-sm text-fg-muted">{t('me.visibility.loading')}</p>
                )}
                {account &&
                    ROW_DEFS.map((row) => (
                        <section key={row.key}>
                            <Eyebrow class="mb-2">{t(row.labelKey)}</Eyebrow>
                            <p class="mb-3 text-sm text-fg-muted">
                                {t(row.descriptionKey)}
                            </p>
                            <div class="flex flex-wrap gap-2">
                                {(['Public', 'Unlisted', 'Private'] as const).map(
                                    (v) => {
                                        const active = account.visibility[row.key] === v;
                                        return (
                                            <button
                                                key={v}
                                                type="button"
                                                onClick={() => onChange(row.key, v)}
                                                disabled={busyKey !== null}
                                                class={`rounded border px-3 py-1.5 text-sm transition-colors disabled:opacity-60 ${
                                                    active
                                                        ? 'border-accent bg-accent text-surface-base'
                                                        : 'border-rule-strong text-fg-primary hover:border-accent'
                                                }`}
                                            >
                                                {t(`common.visibility.${v.toLowerCase()}`)}
                                            </button>
                                        );
                                    },
                                )}
                            </div>
                            <p class="mt-2 text-xs text-fg-muted">
                                {t(row.noteKeys[account.visibility[row.key]])}
                            </p>
                        </section>
                    ))}
            </div>
        </AccountShell>
    );
}
